"use client";

import { ResponsiveLine } from "@nivo/line";
import { gothamFonts, cn } from "@lattice/frontend/lib/utils/ui";
import { compactCardPadding } from "@lattice/frontend/lib/styles/common";

type T_PriceTargetChartProps = {
  currentPrice: number;
  targetLow: number;
  targetMedian: number;
  targetHigh: number;
  historicalData?: Array<{
    date: string;
    price: number;
  }>;
};

type T_CustomLayerProps = {
  series: ReadonlyArray<{
    id: string;
    color: string;
    data: ReadonlyArray<{
      data: { x: number; y: number };
    }>;
  }>;
  lineGenerator: (data: Array<{ x: number; y: number }>) => string | null;
  xScale: (value: number) => number;
  yScale: (value: number) => number;
};

export default function PriceTargetChart({
  currentPrice,
  targetLow,
  targetMedian,
  targetHigh,
  historicalData: _historicalData = [],
}: T_PriceTargetChartProps) {
  // Calculate percentage changes
  const percentChangeLow = ((targetLow - currentPrice) / currentPrice) * 100;
  const percentChangeMedian =
    ((targetMedian - currentPrice) / currentPrice) * 100;
  const percentChangeHigh = ((targetHigh - currentPrice) / currentPrice) * 100;

  // Create responsive spacing based on container width
  // More spacing on larger screens, less on mobile
  const targetPosition = 5; // Moved from 3 to 5 for more spacing

  // Create straight dotted lines from current price to targets
  const createTargetLine = (targetPrice: number) => {
    return [
      { x: 1, y: currentPrice },
      { x: targetPosition, y: targetPrice },
    ];
  };

  // Prepare chart data with current price and target lines
  // Add invisible padding points to force spacing
  const chartData = [
    {
      id: "padding-start",
      color: "transparent",
      data: [{ x: 0, y: currentPrice }],
    },
    {
      id: "Current Price",
      color: "#6b7280", // Gray color
      data: [{ x: 1, y: currentPrice }],
    },
    {
      id: "padding-mid1",
      color: "transparent",
      data: [{ x: 2, y: currentPrice }],
    },
    {
      id: "padding-mid2",
      color: "transparent",
      data: [{ x: 3, y: currentPrice }],
    },
    {
      id: "padding-mid3",
      color: "transparent",
      data: [{ x: 4, y: currentPrice }],
    },
    {
      id: "Target Low",
      color: "#ef4444",
      data: createTargetLine(targetLow),
    },
    {
      id: "Target Median",
      color: "#eab308",
      data: createTargetLine(targetMedian),
    },
    {
      id: "Target High",
      color: "#10b981",
      data: createTargetLine(targetHigh),
    },
    {
      id: "padding-end",
      color: "transparent",
      data: [{ x: 6, y: currentPrice }],
    },
  ];

  // Calculate Y-axis range with padding
  const allPrices = [currentPrice, targetLow, targetMedian, targetHigh];
  const minPrice = Math.min(...allPrices);
  const maxPrice = Math.max(...allPrices);
  const padding = (maxPrice - minPrice) * 0.1;
  const yMin = Math.floor(minPrice - padding);
  const yMax = Math.ceil(maxPrice + padding);

  // Format value for display
  const formatValue = (value: number) => {
    return `$${value.toFixed(2)}`;
  };

  // Format percentage
  const formatPercent = (value: number) => {
    const sign = value >= 0 ? "+" : "";
    return `${sign}${value.toFixed(1)}%`;
  };

  return (
    <div className="w-full flex flex-col md:flex-row gap-4 items-start">
      {/* Chart on the left - 4/5 width */}
      <div className="grow w-full md:w-4/5 h-[380px] sm:h-[420px] md:h-[460px]">
        <ResponsiveLine
          data={chartData}
          margin={{
            top: 20,
            right: 20,
            bottom: 40,
            left: 50,
          }}
          xScale={{ type: "point" }}
          yScale={{
            type: "linear",
            min: yMin,
            max: yMax,
            stacked: false,
            reverse: false,
          }}
          curve="monotoneX"
          axisTop={null}
          axisRight={null}
          axisBottom={{
            tickSize: 0,
            tickPadding: 0,
            tickRotation: 0,
            format: () => "",
          }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            tickValues: 4,
            format: (value) => `$${value}`,
          }}
          colors={(d) => d.color}
          lineWidth={2}
          pointSize={0}
          pointColor={{ theme: "background" }}
          pointBorderWidth={2}
          pointBorderColor={{ from: "serieColor" }}
          enablePoints={false}
          // Use dotted lines for target projections
          defs={[
            {
              id: "dashedLine",
              type: "patternLines",
              background: "transparent",
              color: "inherit",
              rotation: 0,
              lineWidth: 2,
              spacing: 5,
            },
          ]}
          // Apply dotted style to target lines and add price labels
          layers={[
            "grid",
            "markers",
            "axes",
            "areas",
            "crosshair",
            (props: T_CustomLayerProps) => {
              const { series, lineGenerator, xScale, yScale } = props;
              return (
                <>
                  {/* Draw lines with 80% opacity to match circles */}
                  {series.map((serie) => {
                    if (
                      serie.id === "Current Price" ||
                      serie.id.includes("padding")
                    )
                      return null;

                    const d = lineGenerator(
                      serie.data.map((d) => ({
                        x: xScale(d.data.x),
                        y: yScale(d.data.y),
                      })),
                    );

                    return (
                      <path
                        key={serie.id}
                        d={d || ""}
                        fill="none"
                        stroke={serie.color}
                        strokeWidth={3}
                        strokeOpacity={1}
                        style={{ strokeDasharray: "5,5" }}
                      />
                    );
                  })}

                  {/* Add circular points with percentage labels */}
                  {series.map((serie) => {
                    // Skip padding series
                    if (serie.id.includes("padding")) return null;

                    return serie.data.map((point, index: number) => {
                      const x = xScale(point.data.x);
                      const y = yScale(point.data.y);

                      // Only draw Current Price circle for the Current Price series
                      if (serie.id === "Current Price") {
                        return (
                          <g key={`${serie.id}-${index}`}>
                            {/* "Current" label above circle - light gray with 100% opacity */}
                            <text
                              x={x}
                              y={y - 14}
                              textAnchor="middle"
                              fill="#9ca3af"
                              fillOpacity={1}
                              fontSize="11"
                              fontWeight="normal"
                              letterSpacing="0.05em"
                            >
                              Current
                            </text>

                            {/* Circle - light gray with 100% opacity to match text */}
                            <circle
                              cx={x}
                              cy={y}
                              r={8}
                              fill="#9ca3af"
                              fillOpacity={1}
                              stroke="none"
                            />
                          </g>
                        );
                      }

                      // For target lines, only draw the endpoint (position 5)
                      const isEndPoint =
                        index === 1 && point.data.x === targetPosition;
                      if (!isEndPoint) return null;

                      // Calculate percentage change for targets
                      const percentChange =
                        ((point.data.y - currentPrice) / currentPrice) * 100;
                      const percentText = `${percentChange >= 0 ? "+" : ""}${percentChange.toFixed(1)}%`;

                      return (
                        <g key={`${serie.id}-${index}`}>
                          {/* Circle - full opacity for targets */}
                          <circle
                            cx={x}
                            cy={y}
                            r={8}
                            fill={serie.color}
                            fillOpacity={1}
                            stroke="none"
                          />

                          {/* Percentage label on top */}
                          <text
                            x={x}
                            y={y - 14}
                            textAnchor="middle"
                            fill={serie.color}
                            fillOpacity={1}
                            fontSize="13"
                            fontWeight="bold"
                          >
                            {percentText}
                          </text>
                        </g>
                      );
                    });
                  })}
                </>
              );
            },
            "slices",
            "mesh",
            "legends",
          ]}
          enableGridX={false}
          enableGridY={false}
          enableArea={false}
          areaOpacity={0.1}
          useMesh={true}
          isInteractive={true}
          animate={true}
          motionConfig="gentle"
          legends={[]}
          theme={{
            grid: {
              line: {
                stroke: "#374151",
                strokeDasharray: "2,4",
              },
            },
            axis: {
              ticks: {
                text: {
                  fill: "#9ca3af",
                  fontSize: 10,
                },
                line: {
                  stroke: "rgba(255, 255, 255, 0.1)",
                },
              },
              domain: {
                line: {
                  stroke: "rgba(255, 255, 255, 0.1)",
                },
              },
            },
            crosshair: {
              line: {
                stroke: "#6b7280",
                strokeWidth: 1,
                strokeOpacity: 0.5,
              },
            },
          }}
          tooltip={() => null}
        />
      </div>

      {/* Cards stacked on the right - 1/5 width, height matches y-axis */}
      <div className="flex flex-row md:flex-col gap-3 w-full md:w-1/5 md:h-[460px]">
        {/* High Target */}
        <div
          className={cn(
            "rounded-lg flex flex-col justify-center items-center backdrop-blur-sm shadow-sm flex-1",
            compactCardPadding,
          )}
          style={{
            backgroundColor: "#10b98115",
            borderWidth: "2px",
            borderColor: "#10b98150",
          }}
        >
          <p
            className="text-xs font-normal uppercase tracking-wider mb-3 text-center"
            style={{ ...gothamFonts.sidebarSubheader, color: "#10b981" }}
          >
            High Target
          </p>
          <div className="flex flex-col items-center justify-center gap-1">
            <p className="text-base sm:text-lg font-semibold text-foreground">
              {formatValue(targetHigh)}
            </p>
            <span
              className="text-sm font-semibold whitespace-nowrap"
              style={{
                color: percentChangeHigh >= 0 ? "#10b981" : "#ef4444",
              }}
            >
              {formatPercent(percentChangeHigh)}
            </span>
          </div>
        </div>
        {/* Median Target */}
        <div
          className={cn(
            "rounded-lg flex flex-col justify-center items-center backdrop-blur-sm shadow-sm flex-1",
            compactCardPadding,
          )}
          style={{
            backgroundColor: "#eab30815",
            borderWidth: "2px",
            borderColor: "#eab30850",
          }}
        >
          <p
            className="text-xs font-normal uppercase tracking-wider mb-3 text-center"
            style={{ ...gothamFonts.sidebarSubheader, color: "#eab308" }}
          >
            Median Target
          </p>
          <div className="flex flex-col items-center justify-center gap-1">
            <p className="text-base sm:text-lg font-semibold text-foreground">
              {formatValue(targetMedian)}
            </p>
            <span
              className="text-sm font-semibold whitespace-nowrap"
              style={{
                color: percentChangeMedian >= 0 ? "#10b981" : "#ef4444",
              }}
            >
              {formatPercent(percentChangeMedian)}
            </span>
          </div>
        </div>
        {/* Low Target */}
        <div
          className={cn(
            "rounded-lg flex flex-col justify-center items-center backdrop-blur-sm shadow-sm flex-1",
            compactCardPadding,
          )}
          style={{
            backgroundColor: "#ef444415",
            borderWidth: "2px",
            borderColor: "#ef444450",
          }}
        >
          <p
            className="text-xs font-normal uppercase tracking-wider mb-3 text-center"
            style={{ ...gothamFonts.sidebarSubheader, color: "#ef4444" }}
          >
            Low Target
          </p>
          <div className="flex flex-col items-center justify-center gap-1">
            <p className="text-base sm:text-lg font-semibold text-foreground">
              {formatValue(targetLow)}
            </p>
            <span
              className="text-sm font-semibold whitespace-nowrap"
              style={{
                color: percentChangeLow >= 0 ? "#10b981" : "#ef4444",
              }}
            >
              {formatPercent(percentChangeLow)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
